
import React from 'react';
import { Angry, Frown, Meh, Smile, SmilePlus } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { useUser } from '@/context/UserContext';
import analytics from '@/utils/analytics';

interface MoodTrackerProps {
  lessonDay: number;
  moodRating: number | null;
  setMoodRating: (rating: number) => void;
}

const MoodTracker: React.FC<MoodTrackerProps> = ({ 
  lessonDay, 
  moodRating, 
  setMoodRating 
}) => {
  const { user } = useUser();
  
  const handleMoodRating = async (rating: number) => {
    setMoodRating(rating);
    
    // If user is logged in, save mood data to mood_logs table
    if (user?.id) {
      try {
        const moodLabels = ['Very Poor', 'Poor', 'Neutral', 'Good', 'Excellent'];
        
        await supabase.from('mood_logs').insert({
          user_id: user.id,
          mood_rating: rating,
          mood_label: moodLabels[rating - 1],
          associated_lesson: lessonDay,
          notes: 'Recorded from daily content tracker'
        });
        
        // Don't show toast here to avoid too many notifications
      } catch (error) {
        console.error('Error saving mood data:', error);
      }
    }
  };
  
  const moodIcons = [
    { icon: Angry, label: 'Very Poor' },
    { icon: Frown, label: 'Poor' },
    { icon: Meh, label: 'Neutral' },
    { icon: Smile, label: 'Good' },
    { icon: SmilePlus, label: 'Excellent' }
  ];
  
  return (
    <div className="bg-white p-4 rounded-xl border border-hormony-beige/50 shadow-sm">
      <h3 className="font-semibold text-hormony-charcoal mb-3">
        How are you feeling today?
      </h3>
      <div className="flex justify-between mb-3">
        {moodIcons.map((mood, index) => (
          <button
            key={index}
            type="button"
            onClick={() => handleMoodRating(index + 1)}
            className={`flex flex-col items-center transition-all ${
              moodRating === index + 1 
                ? 'scale-110 text-hormony-coral' 
                : 'text-hormony-charcoal/50 hover:text-hormony-charcoal/80'
            }`}
          >
            <mood.icon size={28} />
            <span className="text-xs mt-1">{mood.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default MoodTracker;
